import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { useSaveAvailabilityPreferencesMutation } from '@/modules/availability/mutations/useSaveAvailabilityPreferencesMutation';
import {
  fromAvailabilityPreferencesResponse,
  type InterviewPreferences,
  toAvailabilityPreferencesRequest,
  weekdays,
  type WeeklyAvailability,
  weeklyAvailabilityErrors,
} from '@/modules/availability/preferences';
import { useAvailabilityPreferencesQuery } from '@/modules/availability/queries/useAvailabilityPreferencesQuery';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { FreePlanAutomationCta } from '@/modules/hiring-manager/components/freePlanAutomationCta/FreePlanAutomationCta';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { SettingRow } from '@/modules/hiring-manager/components/SettingRow';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { Button } from '@/modules/ui/components/button/Button';
import { Combobox } from '@/modules/ui/components/combobox/Combobox';
import { SavedIndicator } from '@/modules/ui/components/saved-indicator/SavedIndicator';
import { useSavedFlash } from '@/modules/ui/components/saved-indicator/useSavedFlash';
import { SegmentedControl } from '@/modules/ui/components/segmented-control/SegmentedControl';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import { WeeklyWindowsEditor } from './WeeklyWindowsEditor';

const durations = ['30', '45', '60', '90'] as const;
type Duration = (typeof durations)[number];

const timezoneOptions = ['UTC', ...Intl.supportedValuesOf('timeZone')].map(
  (timezone) => ({ value: timezone, label: timezone.replaceAll('_', ' ') }),
);

function PageHeading() {
  return (
    <div>
      <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em]">
        Availability
      </h1>
      <p className="text-muted-foreground mt-1 text-[13px]">
        The hours Finlay may offer candidates for interviews.
      </p>
    </div>
  );
}

/**
 * Interview length, timezone and the recurring weekly windows Finlay offers.
 * Saves on change; the server's preferences are the source of truth, so the
 * controls show the saved values and a change that fails to save reverts.
 */
export function HMAvailability() {
  const currentUserQuery = useCurrentUserQuery();
  const userId = currentUserQuery.data?.id;
  const { plan, isLoading: isPlanLoading } = useWorkspacePlan();
  const isOnFreePlan = plan === 'free';
  // Skipped on Free, where the preferences are never read; still fetched while
  // the plan is unknown, so a paid tenant waits on nothing.
  const preferencesQuery = useAvailabilityPreferencesQuery(
    isOnFreePlan ? undefined : userId,
  );
  const saveMutation = useSaveAvailabilityPreferencesMutation();
  const [searchParams] = useSearchParams();
  const { isSaved, markSaved, clearSaved } = useSavedFlash();
  // Held only while an edit is in flight or cannot be saved yet: the time
  // selects have to show an end-before-start state the server never sees.
  const [draftSchedule, setDraftSchedule] = useState<WeeklyAvailability | null>(
    null,
  );

  if (
    isPlanLoading ||
    currentUserQuery.isLoading ||
    preferencesQuery.isLoading
  ) {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="divide-border flex max-w-[720px] flex-col divide-y border-y">
          <Skeleton className="my-5 h-8 w-full" />
          <Skeleton className="my-5 h-8 w-full" />
        </div>
      </div>
    );
  }

  // Availability preferences are a paid setting. Free tenants see the plan
  // comparison instead of controls they cannot save.
  if (isOnFreePlan) {
    return (
      <div className="flex flex-col gap-8">
        <PageHeading />
        <FreePlanAutomationCta
          surface="availability"
          to={`/hm/billing${toSearchSuffix(searchParams)}`}
        />
      </div>
    );
  }

  if (currentUserQuery.isError || !userId || preferencesQuery.isError) {
    return (
      <HMLoadError
        description="We could not load your interview preferences. Check your connection and try again."
        isRetrying={preferencesQuery.isFetching || currentUserQuery.isFetching}
        onRetry={() => {
          if (currentUserQuery.isError) void currentUserQuery.refetch();
          else void preferencesQuery.refetch();
        }}
        title="Interview preferences could not be loaded"
      />
    );
  }

  const savedPreferences = fromAvailabilityPreferencesResponse(
    preferencesQuery.data ?? null,
  );
  // Nothing is stored for a first-time hiring manager, so the defaults below
  // are a suggestion until they save them rather than hours Finlay already has.
  const hasSavedPreferences = preferencesQuery.data != null;
  const preferences: InterviewPreferences = {
    ...savedPreferences,
    weeklyAvailability: draftSchedule ?? savedPreferences.weeklyAvailability,
  };
  const scheduleErrors = weeklyAvailabilityErrors(
    preferences.weeklyAvailability,
  );
  const hasScheduleError = weekdays.some((weekday) => scheduleErrors[weekday]);

  // Clear the previous Saved before each attempt so an old success never
  // shows next to the failure of the latest change. The draft is dropped
  // either way: on success and on failure the saved preferences are what the
  // controls must show.
  const save = (next: InterviewPreferences) => {
    clearSaved();
    saveMutation.mutate(
      { body: toAvailabilityPreferencesRequest(next), userId },
      {
        onSuccess: () => {
          setDraftSchedule(null);
          markSaved();
        },
        onError: () => setDraftSchedule(null),
      },
    );
  };

  // A day mid-edit cannot be sent, so the change is only held locally until
  // its windows make sense again.
  const changeSchedule = (weeklyAvailability: WeeklyAvailability) => {
    setDraftSchedule(weeklyAvailability);
    const next = { ...preferences, weeklyAvailability };
    if (
      Object.keys(weeklyAvailabilityErrors(weeklyAvailability)).length === 0
    ) {
      save(next);
    }
  };

  // The controls only save on change, so after a failed save the same values
  // cannot be sent again by picking them once more.
  const lastSave = saveMutation.variables;
  const retrySave = () => {
    if (!lastSave) return;
    clearSaved();
    saveMutation.mutate(lastSave, {
      onSuccess: () => {
        setDraftSchedule(null);
        markSaved();
      },
    });
  };

  const knownTimezone = timezoneOptions.some(
    (option) => option.value === preferences.timezone,
  );
  const availableTimezoneOptions = knownTimezone
    ? timezoneOptions
    : [
        {
          value: preferences.timezone,
          label: `${preferences.timezone} (saved timezone)`,
        },
        ...timezoneOptions,
      ];

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <PageHeading />
        <SavedIndicator isSaved={isSaved} />
      </div>

      {saveMutation.isError && (
        <HMLoadError
          description="Your last change was not saved. Check your connection and try again."
          isRetrying={saveMutation.isPending}
          onRetry={lastSave ? retrySave : undefined}
          title="Interview preferences could not be saved"
        />
      )}

      <p className="max-w-[64ch] text-[13px] leading-[18px] text-neutral-500">
        Finlay offers candidates slots inside the hours you set below. It cannot
        see your calendar, so keep these to hours you can always make. When a
        candidate picks a slot, Finlay confirms it in Slack.
      </p>

      {hasScheduleError && (
        <p
          className="text-destructive max-w-[64ch] text-[13px] leading-[18px]"
          role="alert"
        >
          Fix the weekly hours below before changing anything else. Every change
          saves the whole week at once.
        </p>
      )}

      <div
        aria-busy={saveMutation.isPending}
        className={cn(
          'flex flex-col gap-8 transition-opacity',
          saveMutation.isPending && 'opacity-70',
        )}
      >
        <div className="divide-border flex max-w-[720px] flex-col divide-y border-y">
          <SettingRow
            hint="Finlay proposes slots of this length. You can change it per interview when it asks you."
            label="Interview length"
          >
            {({ labelId, hintId }) => (
              <SegmentedControl<Duration>
                aria-describedby={hintId}
                aria-labelledby={labelId}
                autoFocus
                disabled={saveMutation.isPending || hasScheduleError}
                onValueChange={(value) =>
                  save({
                    ...preferences,
                    defaultMeetingDurationMinutes: Number(value),
                  })
                }
                options={durations.map((value) => ({
                  value,
                  label: `${value} min`,
                }))}
                value={
                  String(preferences.defaultMeetingDurationMinutes) as Duration
                }
              />
            )}
          </SettingRow>

          <SettingRow
            hint="Slots and confirmations are shown in this timezone, for you and for candidates."
            label="Timezone"
          >
            {({ labelId, hintId }) => (
              <Combobox
                aria-describedby={hintId}
                aria-labelledby={labelId}
                className="h-8 w-full text-[13px] md:w-64"
                disabled={saveMutation.isPending || hasScheduleError}
                disableSort
                onChange={(value) =>
                  value !== preferences.timezone &&
                  save({ ...preferences, timezone: value })
                }
                options={availableTimezoneOptions}
                placeholder="Select your timezone"
                searchPlaceholder="Search timezones…"
                value={preferences.timezone}
              />
            )}
          </SettingRow>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              <h2 className="text-[15px] leading-6 font-semibold tracking-[-0.01em]">
                Weekly hours
              </h2>
              <p className="text-muted-foreground max-w-[60ch] text-[13px] leading-[18px]">
                Finlay offers slots inside these hours every week, in your
                timezone.
              </p>
            </div>
            {!hasSavedPreferences && (
              <Button
                disabled={saveMutation.isPending || hasScheduleError}
                onClick={() => save(preferences)}
                size="sm"
                type="button"
                variant="outline"
              >
                Save these hours
              </Button>
            )}
          </div>

          {!hasSavedPreferences && (
            <p className="text-[13px] leading-[18px] text-amber-700">
              These are Finlay&apos;s suggested hours, Monday to Friday, 9:00 to
              17:00. Nothing is saved yet — change them or save them as they
              are.
            </p>
          )}

          <WeeklyWindowsEditor
            disabled={saveMutation.isPending}
            onChange={changeSchedule}
            value={preferences.weeklyAvailability}
          />
        </div>
      </div>
    </div>
  );
}
